import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    ChartBar, Coins, Package, TrendUp, Storefront, ArrowLeft
} from '@phosphor-icons/react';
import { Skeleton } from '../components/ui/skeleton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function SellerAnalytics() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/connect/analytics`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <Layout><div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
            <Skeleton className="h-8 w-48" /><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" />
        </div></Layout>;
    }

    if (!data?.has_account) {
        return (
            <Layout><div className="max-w-3xl mx-auto px-4 py-6">
                <Card className="glass-card"><CardContent className="p-8 text-center">
                    <Storefront size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="font-medium text-foreground">No seller account</p>
                    <p className="text-sm text-muted-foreground mt-1">Create a connected account in the Seller Hub to see analytics</p>
                    <Button onClick={() => navigate('/connect')} className="mt-4 btn-primary">Go to Seller Hub</Button>
                </CardContent></Card>
            </div></Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <Button size="sm" variant="ghost" onClick={() => navigate('/connect')} className="mb-2 text-muted-foreground">
                        <ArrowLeft size={16} className="mr-1" />Seller Hub
                    </Button>
                    <span className="label-text text-boltz-gold text-glow-gold">ANALYTICS</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="analytics-title">
                        Seller Dashboard
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">{data.display_name}</p>
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    {[
                        { label: 'PRODUCTS', val: data.product_count, icon: Package, color: 'text-primary' },
                        { label: 'SALES', val: data.total_sales + data.marketplace_orders, icon: TrendUp, color: 'text-neon-green' },
                        { label: 'REVENUE', val: `${data.marketplace_revenue_boltz} B`, icon: Coins, color: 'text-neon-gold' },
                        { label: 'CONNECT', val: `$${(data.total_revenue_cents / 100).toFixed(0)}`, icon: ChartBar, color: 'text-electric-blue' },
                    ].map(s => (
                        <Card key={s.label} className="glass-card">
                            <CardContent className="p-4 text-center">
                                <s.icon size={20} className={`${s.color} mx-auto mb-1`} />
                                <p className={`text-xl font-black font-mono ${s.color}`}>{s.val}</p>
                                <p className="text-[10px] text-muted-foreground font-display">{s.label}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Top Products */}
                {data.top_products?.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                        <h3 className="font-display text-sm text-muted-foreground tracking-wider mb-3">TOP PRODUCTS</h3>
                        <div className="space-y-2 mb-6">
                            {data.top_products.map((p, i) => (
                                <Card key={p.product_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                            <span className="text-xs font-bold text-primary font-mono">#{i + 1}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-sm text-foreground truncate">{p.name}</p>
                                            <p className="text-[10px] text-muted-foreground">{p.sales} sales</p>
                                        </div>
                                        <Badge className="font-mono text-neon-gold bg-neon-gold/10 border-neon-gold/20">
                                            ${(p.revenue / 100).toFixed(2)}
                                        </Badge>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Recent Orders */}
                {data.recent_orders?.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <h3 className="font-display text-sm text-muted-foreground tracking-wider mb-3">RECENT ORDERS</h3>
                        <div className="space-y-2">
                            {data.recent_orders.map(o => (
                                <Card key={o.order_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-foreground">{o.item_name}</p>
                                            <p className="text-[10px] text-muted-foreground">{new Date(o.created_at).toLocaleDateString()} · {o.delivery_status}</p>
                                        </div>
                                        <span className="font-mono text-neon-gold text-sm">{o.amount} B</span>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
